import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { movieBookingApi } from '../../services/movieBookingApi';
import { connectSeatWebSocket } from '../../services/seatWebSocket';
import { useAuth } from '../../context/AuthContext';
import { getMovieBookingRole, isAdminRole } from './movieBookingRole';
import './MovieBooking.css';

const AdminSeatMap = () => {
  const { showId } = useParams();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user) || getMovieBookingRole() === 'ADMIN';
  const navigate = useNavigate();
  const [seatMap, setSeatMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [wsError, setWsError] = useState(null);

  useEffect(() => {
    let socket;
    const loadSeatMap = async () => {
      try {
        const response = await movieBookingApi.getSeatMap(showId);
        setSeatMap(response.data?.seats || {});
      } catch (error) {
        setSeatMap({});
      } finally {
        setLoading(false);
      }
    };

    if (showId && isAdmin) {
      loadSeatMap();
      socket = connectSeatWebSocket(showId, (event) => {
        if (event.type === 'SEAT_SNAPSHOT' && event.seats) {
          setSeatMap(event.seats);
        }
        if (event.type === 'SEAT_STATUS' && event.seatId) {
          setSeatMap((prev) => ({
            ...prev,
            [event.seatId]: {
              ...(prev[event.seatId] || {}),
              status: event.status,
              selectedBy: event.selectedBy,
              selectionExpiry: event.expiresAt,
            },
          }));
        }
      }, setWsError);
    }

    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [showId, isAdmin]);

  const seats = useMemo(() => Object.entries(seatMap), [seatMap]);
  const seatCounts = useMemo(() => {
    return seats.reduce((acc, [, seatState]) => {
      const status = seatState?.status || 'AVAILABLE';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [seats]);

  if (!isAdmin) {
    return (
      <div className="movie-booking-page">
        <h1 className="movie-booking-title">Admin access required</h1>
        <p className="movie-booking-note">Switch to Admin role to view seat maps.</p>
      </div>
    );
  }

  return (
    <div className="movie-booking-page">
      <div className="movie-booking-header">
        <div>
          <h1 className="movie-booking-title">Seat Map</h1>
          <p className="movie-booking-note">Show ID: {showId}</p>
          {wsError && <p className="movie-booking-note">WebSocket: {wsError}</p>}
        </div>
        <button className="movie-booking-button secondary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <div className="movie-booking-legend">
        <div className="movie-booking-legend-item">
          <span className="movie-booking-legend-swatch status-available" />
          Available ({seatCounts.AVAILABLE || 0})
        </div>
        <div className="movie-booking-legend-item">
          <span className="movie-booking-legend-swatch status-under-process" />
          Under Process ({seatCounts.UNDER_PROCESS || 0})
        </div>
        <div className="movie-booking-legend-item">
          <span className="movie-booking-legend-swatch status-booked" />
          Booked ({seatCounts.BOOKED || 0})
        </div>
      </div>

      {loading && <p>Loading seat map...</p>}
      {!loading && (
        <div className="movie-booking-seat-grid">
          {seats.map(([seatId, seatState]) => {
            const status = seatState?.status || 'AVAILABLE';
            const statusClass = status === 'BOOKED'
              ? 'status-booked'
              : status === 'UNDER_PROCESS'
                ? 'status-under-process'
                : 'status-available';
            return (
              <span
                key={seatId}
                className={`movie-booking-seat ${statusClass}`}
                title={status}
              >
                {seatId}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSeatMap;
